<template>
  <ValidationObserver
    ref="observer"
    v-slot="{ valid, dirty }"
  >
    <div>
      <v-row>
        <v-col
          cols="8"
        >
          <VTextField
            v-model.number="coolingOperatingParameters.hot.temperatureValue"
            :label="$t('massBalance.e4Form.labels.temperatureHot')"
            type="number"
            outlined
            :rules="{ required: true }"
            @input="onInput"
          />
        </v-col>
        <v-col>
          <VSelect
            v-model="coolingOperatingParameters.temperatureUnit"
            :label="$t('massBalance.e4Form.labels.unit')"
            :items="temperatureUnits"
            outlined
            :rules="{ required: true }"
            @input="onInput"
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col>
          <VSelect
            v-model="coolingOperatingParameters.hot.network"
            :label="$t('massBalance.e4Form.labels.network')"
            :items="coolingOperatingParameters.networksList"
            type="number"
            outlined
            :rules="{ required: true }"
            @input="toggleNetWorkOrComponent('hot', 'network')"
          />
        </v-col>
        <v-col>
          <VSelect
            v-model="coolingOperatingParameters.hot.component"
            :label="$t('massBalance.e4Form.labels.component')"
            :items="coolingOperatingParameters.hotComponentsList"
            :disabled="!coolingOperatingParameters.hot.network"
            outlined
            :rules="{ required: true }"
            @input="toggleNetWorkOrComponent('hot', 'component')"
          />
        </v-col>
        <v-col>
          <VSelect
            v-model="coolingOperatingParameters.hot.analysis"
            :label="$t('massBalance.e4Form.labels.analysis')"
            :items="coolingOperatingParameters.hotAnalysisList"
            :disabled="!coolingOperatingParameters.hot.component"
            outlined
            :rules="{ required: true }"
            @input="onInput"
          />
        </v-col>
      </v-row>

      <div
        style="border-top: 3px solid #9e9e9e; width: 100%; height: 1px; margin-bottom: 20px;"
      />

      <v-row>
        <v-col
          cols="8"
        >
          <VTextField
            v-model.number="coolingOperatingParameters.cold.temperatureValue"
            :label="$t('massBalance.e4Form.labels.temperatureCold')"
            outlined
            type="number"
            :rules="{ required: true }"
            @input="onInput"
          />
        </v-col>
        <v-col>
          <VSelect
            v-model="coolingOperatingParameters.temperatureUnit"
            :label="$t('massBalance.e4Form.labels.unit')"
            :items="temperatureUnits"
            outlined
            disabled
            :rules="{ required: true }"
            @input="onInput"
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col>
          <VSelect
            v-model="coolingOperatingParameters.cold.network"
            :label="$t('massBalance.e4Form.labels.network')"
            :items="coolingOperatingParameters.networksList"
            type="number"
            outlined
            :rules="{ required: true }"
            @input="toggleNetWorkOrComponent('cold', 'network')"
          />
        </v-col>
        <v-col>
          <VSelect
            v-model="coolingOperatingParameters.cold.component"
            :label="$t('massBalance.e4Form.labels.component')"
            :items="coolingOperatingParameters.coldComponentsList"
            :disabled="!coolingOperatingParameters.cold.network"
            outlined
            :rules="{ required: true }"
            @input="toggleNetWorkOrComponent('cold', 'component')"
          />
        </v-col>
        <v-col>
          <VSelect
            v-model="coolingOperatingParameters.cold.analysis"
            :label="$t('massBalance.e4Form.labels.analysis')"
            :items="coolingOperatingParameters.coldAnalysisList"
            :disabled="!coolingOperatingParameters.cold.component"
            outlined
            :rules="{ required: true }"
            @input="onInput"
          />
        </v-col>
      </v-row>
    </div>
    <SlotScopeWatcher
      :value="dirty"
      @change="$emit('dirty-changed', dirty)"
    />
    <SlotScopeWatcher
      :value="valid"
      @change="$emit('valid-changed', valid)"
    />
  </ValidationObserver>
</template>

<script>
import SlotScopeWatcher from '@/components/shared/widgets/forms/validationHelper/SlotScopeWatcher';
import { ValidationObserver } from 'vee-validate';
import VSelect from '@/components/shared/widgets/forms/VuetifyInputs/VSelect.vue';
import VTextField from '@/components/shared/widgets/forms/VuetifyInputs/VTextField.vue';
import { mapGetters, mapActions } from 'vuex';
import { FEED_WATER_TEMPERATURE_UNITS } from '@/aquassist-shared/constants/massBalance';
import { cloneDeep } from 'lodash';
import { COOLING } from '@/aquassist-shared/constants/types/network';


export default {
    name: 'CoolingOperatingParameters',
    components: {
        ValidationObserver,
        SlotScopeWatcher,
        VSelect,
        VTextField,
    },
    props: {
        editMode: {
            type: Boolean,
            default: false,
        },

    },

    data() {
        return {
            coolingOperatingParameters: {
                temperatureUnit: null,
                hot: {
                    temperatureValue: null,
                    network: null,
                    component: null,
                    analysis: null,
                    // temporary
                    componentsList: [],
                    analysisList: [],

                },
                cold: {
                    temperatureValue: null,
                    network: null,
                    component: null,
                    analysis: null,
                    // temporary
                    componentsList: [],
                    analysisList: [],
                },
                networksList: [],
                // hot
                hotComponentsList: [],
                hotAnalysisList: [],
                // cold
                coldComponentsList: [],
                coldAnalysisList: [],
            },
            initialized: false,
        };
    },
    computed: {
        ...mapGetters(['site', 'userLang', 'e4Config']),
        temperatureUnits() {
            const units = FEED_WATER_TEMPERATURE_UNITS.map(unit => ({
                name: `°${unit}`,
                id: unit,
            }));
            return units;
        },
    },
    watch: {
        // Pour la 2eme ligne de chaque section
        // Get des component et analysis en fonction du champ précédent
        // Je réinitialise les champs suivants si le champ en question change

        'coolingOperatingParameters.hot.network': async function (val) {
            const componentList = await this.getComponentsListByNetwork(val);
            this.coolingOperatingParameters.hotComponentsList = componentList;
            this.onInput();
        },
        'coolingOperatingParameters.hot.component': async function (val) {
            const analysisList = await this.getAnalysisListByComponent(val);
            this.coolingOperatingParameters.hotAnalysisList = analysisList;
            this.onInput();
        },
        'coolingOperatingParameters.cold.network': async function (val) {
            const componentList = await this.getComponentsListByNetwork(val);
            this.coolingOperatingParameters.coldComponentsList = componentList;
            this.onInput();
        },
        'coolingOperatingParameters.cold.component': async function (val) {
            const analysisList = await this.getAnalysisListByComponent(val);
            this.coolingOperatingParameters.coldAnalysisList = analysisList;
            this.onInput();
        },
    },


    async mounted() {
        if (this.editMode) {
            this.coolingOperatingParameters.hot = cloneDeep(this.e4Config.coolingOperatingParameters.hot);
            this.coolingOperatingParameters.cold = cloneDeep(this.e4Config.coolingOperatingParameters.cold);
            this.coolingOperatingParameters.temperatureUnit = this.e4Config.coolingOperatingParameters.temperatureUnit;
        }

        await this.getNetworks();
        if (!this.editMode) this.initialized = true;
        this.onInput();
    },

    methods: {
        ...mapActions(['setE4Config']),
        async getNetworks() {
            const { data } = await this.$rq.getAllSiteNetwork(this.site.id, COOLING.toLowerCase());

            this.coolingOperatingParameters.networksList = data;
        },
        async getComponentsListByNetwork(networkId) {
            try {
                const { data } = await this.$rq.getComponentsListByNetworkId(this.site.id, networkId);
                return data;
            } catch (error) {
                return null;
            }
        },
        async getAnalysisListByComponent(componentId) {
            try {
                const { data } = await this.$rq.getComponentConfigurations(componentId);
                const extractedData = data.configurations.map(item => ({
                    id: item.id,
                    name: item.analysisType?.name[this.userLang] || '',
                }));

                return extractedData;
            } catch (error) {
                return null;
            }
        },
        async onInput() {
            this.setE4Config({ coolingOperatingParameters: cloneDeep(this.coolingOperatingParameters) });
        },
        toggleNetWorkOrComponent(hotOrCold, networkOrComponent) {
            if (networkOrComponent === 'network') {
                this.coolingOperatingParameters[hotOrCold].component = null;
                this.coolingOperatingParameters[hotOrCold].analysis = null;
            }
            if (networkOrComponent === 'component') {
                this.coolingOperatingParameters[hotOrCold].analysis = null;
            }
            this.onInput();
        },
    },
};
</script>
